import { Directive, ElementRef, Input, NgZone, Renderer2 } from '@angular/core';
import { DataObject } from '@shared/models/data-object.model';
import { ICatalog } from '@shared/models/icommons.model';
import { filter, switchMap, take } from 'rxjs';
import { TrackUserActionService } from './track-user-actions.service';

export interface TrackActionPayloadTransformer {
  transform<T = any>(...args: T[]): DataObject;
}

export type TrackActionPayloadFunction = (...args: any) => DataObject;

@Directive({
  selector: '[miqTrackAction]',
  standalone: false,
})
export class MiqTrackUserActionsDirective {
  @Input() trackActionName: string;
  @Input() trackActionData: any;
  @Input() trackActionPayloadTransformer?: TrackActionPayloadTransformer | TrackActionPayloadFunction;
  @Input() isActionTracked?: boolean = true;

  #elementClickListener: Function;

  constructor(
    private trackUserActionServ: TrackUserActionService,
    protected elRef: ElementRef,
    private ngZone: NgZone,
    private renderer: Renderer2
  ) {}

  #onListenerTriggered = (evt: MouseEvent) => {
    // while testing, evt is the native element sent to this handler, that's why we compare evt with elRef.nativeElement
    if ((this.isActionTracked && this.elRef.nativeElement.contains(evt.target)) || evt === this.elRef.nativeElement)
      this.#registerAction();
  };

  #toRequest = (action: ICatalog) => {
    let actionDetails = this.trackActionData;

    if (this.trackActionPayloadTransformer) {
      actionDetails =
        typeof this.trackActionPayloadTransformer === 'function'
          ? this.trackActionPayloadTransformer(this.trackActionData)
          : this.trackActionPayloadTransformer.transform(this.trackActionData);
    }

    return this.trackUserActionServ.registerAction({ actionId: action.key as number, actionDetails });
  };

  #registerAction() {
    if (this.trackActionName == null) return;
    this.trackUserActionServ
      .getActionByKeyOrValue('value', this.trackActionName)
      .pipe(take(1), filter(Boolean), switchMap(this.#toRequest))
      .subscribe();
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      this.#elementClickListener = this.renderer.listen(this.elRef.nativeElement, 'click', this.#onListenerTriggered);
    });
  }

  ngOnDestroy() {
    if (typeof this.#elementClickListener === 'function') this.#elementClickListener();
  }
}
